import { useState, useCallback } from "react";
import { useAuth, API } from "../App";
import Layout from "../components/Layout";
import axios from "axios";
import { toast } from "sonner";
import { useDropzone } from "react-dropzone";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import {
  Upload,
  FileSpreadsheet,
  CheckCircle,
  XCircle,
  Download,
  Loader2,
  Info,
  Zap
} from "lucide-react";

const ImportCSV = () => {
  const { token } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [progress, setProgress] = useState(0);

  const onDrop = useCallback(async (acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file) return;

    const validExts = ['.csv', '.pdf', '.xlsx', '.xls'];
    const ext = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    if (!validExts.includes(ext)) {
      toast.error("Format accepté: CSV (.csv), Excel (.xlsx) ou PDF (.pdf)");
      return;
    }

    setUploading(true);
    setResult(null);
    setProgress(0);

    const formData = new FormData();
    formData.append('file', file);

    // Simulate progress
    const progressInterval = setInterval(() => {
      setProgress(prev => Math.min(prev + 10, 90));
    }, 200);

    try {
      const response = await axios.post(`${API}/products/import-csv`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      clearInterval(progressInterval);
      setProgress(100);
      setResult(response.data);
      toast.success(`${response.data.imported_count} produit(s) importé(s) avec succès!`);
    } catch (error) {
      clearInterval(progressInterval);
      toast.error(error.response?.data?.detail || "Erreur lors de l'import");
      setResult({ error: true, message: error.response?.data?.detail || "Erreur" });
    } finally {
      setUploading(false);
    }
  }, [token]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls']
    },
    maxFiles: 1,
    disabled: uploading
  });

  const downloadTemplate = () => {
    const csvContent = `name,batch_number,expiry_date,quantity,unit_price,supplier,category,pharma_class,form,dosage,laboratory,weight_kg
Paracetamol 500mg,LOT-2026-001,15/06/2027,100,500,Pharma CI,Medicament,Antalgique,Comprime,500mg,Sanofi,0.05
Amoxicilline 250mg,LOT-2026-002,20/03/2027,50,1500,MedSupply,Medicament,Antibiotique,Gelule,250mg,GSK,0.03
Ibuprofene 400mg,LOT-2026-003,31/12/2026,75,750,HealthCorp,Medicament,AINS,Comprime,400mg,Pfizer,0.04
Metformine 850mg,LOT-2026-004,10/09/2027,200,1800,Denk Pharma,Medicament,Antidiabetique,Comprime,850mg,Merck,0.06
Omeprazole 20mg,LOT-2026-005,01/01/2028,120,2500,Ratiopharm,Medicament,Anti-ulcereux,Gelule,20mg,Ratiopharm,0.02`;

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'template_produits_cmes.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <Layout>
      <div className="p-6 md:p-8 space-y-8" data-testid="import-csv-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-outfit font-bold text-white">
              Import CSV
            </h1>
            <p className="text-slate-400 mt-1">
              Importez vos produits en masse avec catégorisation automatique
            </p>
          </div>
          
          <Button 
            variant="outline"
            onClick={downloadTemplate}
            data-testid="download-template-btn"
            className="border-sky-500/30 text-sky-400 hover:bg-sky-500/10 gap-2"
          >
            <Download className="w-4 h-4" />
            Télécharger le template
          </Button>
        </div>

        {/* Info Card */}
        <Card className="glass-card border-sky-500/20">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-sky-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-slate-300">
                  Le système analyse automatiquement les noms de produits et attribue une catégorie 
                  (antibiotique, antalgique, antipaludéen, etc.) grâce à notre algorithme intelligent.
                </p>
                <div className="flex flex-wrap gap-2 mt-3">
                  {["antibiotique", "antalgique", "antidiabétique", "antipaludéen", "vitamine"].map(cat => (
                    <Badge key={cat} variant="outline" className="border-white/10 text-slate-400">
                      {cat}
                    </Badge>
                  ))}
                  <Badge variant="outline" className="border-white/10 text-slate-400">+5 autres</Badge>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Dropzone */}
        <Card className="glass-card">
          <CardContent className="p-8">
            <div
              {...getRootProps()}
              data-testid="csv-dropzone"
              className={`
                dropzone relative p-12 rounded-xl text-center cursor-pointer
                transition-all duration-300 overflow-hidden
                ${isDragActive ? 'active border-sky-500 bg-sky-500/5' : 'border-white/10'}
                ${uploading ? 'pointer-events-none opacity-70' : ''}
              `}
            >
              <input {...getInputProps()} data-testid="csv-file-input" />
              
              {/* Scan line effect */}
              {isDragActive && <div className="scan-line" />}
              
              <div className="relative z-10">
                {uploading ? (
                  <div className="space-y-4">
                    <Loader2 className="w-16 h-16 text-sky-400 mx-auto animate-spin" />
                    <p className="text-white font-medium">Analyse en cours...</p>
                    <Progress value={progress} className="w-64 mx-auto h-2" />
                    <p className="text-slate-500 text-sm">{progress}%</p>
                  </div>
                ) : isDragActive ? (
                  <div className="space-y-4">
                    <div className="w-20 h-20 rounded-full bg-sky-500/20 mx-auto flex items-center justify-center pulse-glow">
                      <Zap className="w-10 h-10 text-sky-400" />
                    </div>
                    <p className="text-xl text-sky-400 font-outfit font-bold">
                      Déposez le fichier ici
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="w-20 h-20 rounded-full bg-slate-800 mx-auto flex items-center justify-center">
                      <FileSpreadsheet className="w-10 h-10 text-slate-400" />
                    </div>
                    <div>
                      <p className="text-xl text-white font-outfit font-semibold">
                        Glissez votre fichier ici
                      </p>
                      <p className="text-slate-500 mt-2">
                        ou cliquez pour parcourir vos fichiers
                      </p>
                    </div>
                    <div className="flex items-center justify-center gap-4 text-sm text-slate-600">
                      <span>Formats: .csv, .xlsx, .pdf</span>
                      <span>•</span>
                      <span>Encodage CSV: UTF-8</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Results */}
        {result && !result.error && (
          <Card className="glass-card border-emerald-500/20">
            <CardHeader>
              <CardTitle className="text-white font-outfit flex items-center gap-2">
                <CheckCircle className="w-6 h-6 text-emerald-400" />
                Import Terminé
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                  <p className="text-emerald-400 text-3xl font-bold font-outfit">
                    {result.imported_count}
                  </p>
                  <p className="text-slate-400 text-sm">Produits importés</p>
                </div>
                <div className="p-4 rounded-lg bg-red-500/100/10 border border-red-500/20">
                  <p className="text-red-400 text-3xl font-bold font-outfit">
                    {result.errors_count}
                  </p>
                  <p className="text-slate-400 text-sm">Erreurs</p>
                </div>
                <div className="p-4 rounded-lg bg-sky-500/10 border border-sky-500/20">
                  <p className="text-sky-400 text-3xl font-bold font-outfit">
                    {new Set(result.imported?.map(p => p.category)).size}
                  </p>
                  <p className="text-slate-400 text-sm">Catégories détectées</p>
                </div>
              </div>

              {/* Imported products list */}
              {result.imported?.length > 0 && (
                <div className="space-y-2">
                  <p className="text-slate-400 text-sm font-medium mb-3">Produits importés:</p>
                  <div className="max-h-64 overflow-y-auto space-y-2">
                    {result.imported.map((item, index) => (
                      <div 
                        key={index}
                        className="flex items-center justify-between p-3 rounded-lg bg-slate-950/50 border border-white/5"
                      >
                        <div className="flex items-center gap-3">
                          <CheckCircle className="w-4 h-4 text-emerald-400" />
                          <span className="text-white">{item.name}</span>
                        </div>
                        <Badge variant="outline" className="border-sky-500/30 text-sky-400">
                          {item.category}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Errors list */}
              {result.errors?.length > 0 && (
                <div className="mt-6 space-y-2">
                  <p className="text-red-400 text-sm font-medium mb-3">Erreurs:</p>
                  {result.errors.map((err, index) => (
                    <div 
                      key={index}
                      className="flex items-center gap-3 p-3 rounded-lg bg-red-500/100/10 border border-red-500/20"
                    >
                      <XCircle className="w-4 h-4 text-red-400" />
                      <span className="text-slate-300">Ligne {err.row}: {err.error}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Error Result */}
        {result?.error && (
          <Card className="glass-card border-red-500/20">
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <XCircle className="w-6 h-6 text-red-400" />
                <p className="text-red-400">{result.message}</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Format Guide */}
        <Card className="glass-card">
          <CardHeader>
            <CardTitle className="text-white font-outfit">Format du fichier CSV</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left p-3 text-slate-400">Colonne</th>
                    <th className="text-left p-3 text-slate-400">Description</th>
                    <th className="text-left p-3 text-slate-400">Exemple</th>
                    <th className="text-left p-3 text-slate-400">Obligatoire</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-white/5">
                    <td className="p-3 text-sky-400 font-mono">name</td>
                    <td className="p-3 text-slate-300">Nom du produit</td>
                    <td className="p-3 text-slate-500">Paracétamol 500mg</td>
                    <td className="p-3"><CheckCircle className="w-4 h-4 text-emerald-400" /></td>
                  </tr>
                  <tr className="border-b border-white/5">
                    <td className="p-3 text-sky-400 font-mono">batch_number</td>
                    <td className="p-3 text-slate-300">Numéro de lot</td>
                    <td className="p-3 text-slate-500">LOT-2024-001</td>
                    <td className="p-3"><CheckCircle className="w-4 h-4 text-emerald-400" /></td>
                  </tr>
                  <tr className="border-b border-white/5">
                    <td className="p-3 text-sky-400 font-mono">expiry_date</td>
                    <td className="p-3 text-slate-300">Date d'expiration (YYYY-MM-DD)</td>
                    <td className="p-3 text-slate-500">2025-06-15</td>
                    <td className="p-3"><CheckCircle className="w-4 h-4 text-emerald-400" /></td>
                  </tr>
                  <tr className="border-b border-white/5">
                    <td className="p-3 text-sky-400 font-mono">quantity</td>
                    <td className="p-3 text-slate-300">Quantité en stock</td>
                    <td className="p-3 text-slate-500">100</td>
                    <td className="p-3"><CheckCircle className="w-4 h-4 text-emerald-400" /></td>
                  </tr>
                  <tr className="border-b border-white/5">
                    <td className="p-3 text-sky-400 font-mono">unit_price</td>
                    <td className="p-3 text-slate-300">Prix unitaire (FCFA)</td>
                    <td className="p-3 text-slate-500">500</td>
                    <td className="p-3"><CheckCircle className="w-4 h-4 text-emerald-400" /></td>
                  </tr>
                  <tr>
                    <td className="p-3 text-sky-400 font-mono">supplier</td>
                    <td className="p-3 text-slate-300">Nom du fournisseur</td>
                    <td className="p-3 text-slate-500">Pharma CI</td>
                    <td className="p-3 text-slate-500">Optionnel</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default ImportCSV;
